'use client';

/**
 * DistanceFAQ.tsx
 * Single-open accordion FAQ for the Distance product page.
 *
 * Pattern: mirrors Super Fingers SF8 FAQ (SuperFingersPageClient.tsx §8):
 * - React state: openIndex — only one item open at a time
 * - Clicking open item collapses it; clicking closed item opens it and closes prior
 * - Chevron rotates 90deg when open (CSS .sv2-faq-item--open)
 *
 * Uses the existing .sv2-faq-item / .sv2-faq__answer CSS already in globals.css.
 */

import { useState } from 'react';

interface FAQItem {
  question: React.ReactNode;
  answer: React.ReactNode;
}

interface Props {
  items: FAQItem[];
}

export default function DistanceFAQ({ items }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(0); // first open by default

  return (
    <>
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div
            key={i}
            className={`sv2-faq-item${isOpen ? ' sv2-faq-item--open' : ''}`}
          >
            <button
              type="button"
              className="sv2-faq-item__summary"
              aria-expanded={isOpen}
              aria-controls={`distance-faq-answer-${i}`}
              id={`distance-faq-question-${i}`}
              onClick={() => setOpenIndex(isOpen ? null : i)}
            >
              {item.question}
            </button>
            {isOpen && (
              <div
                id={`distance-faq-answer-${i}`}
                role="region"
                aria-labelledby={`distance-faq-question-${i}`}
                className="sv2-faq__answer"
              >
                {item.answer}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
